import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * useShortlistStore
 *
 * Session-only record of pairs shortlisted from the Candidate Profile view,
 * so the "Shortlisted" tag shows immediately on every Talent Mobility table
 * (Role Detail, Redeployment Center) without a refetch. Across reloads the
 * source of truth is the backend: POST /talent-mobility/shortlist persists
 * the pair and /role-successor-analytics returns it as the `shortlisted`
 * flag ShortlistTag receives via props. Deliberately NOT persisted to
 * localStorage — a persisted entry would keep the tag alive even after the
 * API says the pair is no longer shortlisted.
 */
const keyFor = (roleId, personId) => `${roleId}::${personId}`;

const useShortlistStore = create(
  devtools(
    (set, get) => ({
      // keyed by `${roleId}::${personId}` -> { shortlistedAt, notifyEmployee, notifyLineManager }
      shortlisted: {},

      shortlistCandidate: (roleId, personId, notifyEmployee, notifyLineManager, notifyHiringManager) =>
        set((state) => ({
          shortlisted: {
            ...state.shortlisted,
            [keyFor(roleId, personId)]: { shortlistedAt: Date.now(), notifyEmployee, notifyLineManager, notifyHiringManager },
          },
        })),

      // Drops one pair's session tag — used by useRedeploymentPlanStore.reset()
      // to take back shortlists made inside the redeployment flow (those are
      // store-only drafts, never persisted via POST /talent-mobility/shortlist,
      // so removing the entry is all it takes to un-shortlist them).
      removeShortlist: (roleId, personId) =>
        set((state) => {
          const key = keyFor(roleId, personId);
          if (!(key in state.shortlisted)) return state;
          const next = { ...state.shortlisted };
          delete next[key];
          return { shortlisted: next };
        }),

      isShortlisted: (roleId, personId) => Boolean(get().shortlisted[keyFor(roleId, personId)]),
    }),
    { name: 'ShortlistStore' }
  )
);

// The store used to be persisted under this key — drop the stale entry so
// browsers that shortlisted before the change don't keep dead data around.
try {
  window.localStorage.removeItem('tm-shortlist');
} catch {
  // storage unavailable (SSR/blocked) — nothing to clean up
}

// Stable primitive selector — avoids the "new object every call" trap that
// caused useMatchThresholds' infinite-loop bug elsewhere in Talent Mobility.
export const useIsShortlisted = (roleId, personId) => {
  const entry = useShortlistStore((state) => state.shortlisted[keyFor(roleId, personId)]);
  return useMemo(() => Boolean(entry), [entry]);
};

export default useShortlistStore;
